// buildMergePlan.ts — Phase 2 of the mergeFile pipeline (FER-10394).
//
// Owns: snapshot-vs-reconstruction selection, marker-rescue, stale-marker
// detection (pre + post accumulator), accumulator-base fallback, pre-merge
// with accumulator, baseMismatchSkipped detection, and the variant-selection
// step that emits the discriminated `MergePlan`.
//
// Behavioral equivalence claim: for every input combination, the returned
// `MergePlan` carries the same effective `theirs`, `mergeBase`,
// `conflictReasonHint`, and (implicitly through `kind`) the same flow-control
// decision the pre-refactor `mergeFile` would have taken into Phase 3.
//
// The boolean trio (`useAccumulatorAsMergeBase`, `baseMismatchSkipped`,
// `ghostReconstructed`) is now internal to this function. Callers consume
// only the `MergePlan` discriminant + fields.

import type { GitClient } from "../git/GitClient.js";
import { hasConflictMarkerStrings } from "../conflict-utils.js";
import { threeWayMerge } from "../ThreeWayMerge.js";
import type { ConflictReason, StoredPatch } from "../types.js";
import type { MergeAccumulator } from "../accumulator/MergeAccumulator.js";
import type { MergePlan, ResolvedInputs } from "./MergePlan.js";

export interface BuildMergePlanArgs {
    inputs: ResolvedInputs;
    patch: StoredPatch;
    filePath: string;
    accumulator: MergeAccumulator;
    tempGit: GitClient;
    tempDir: string;
    applyPatchToContent: (
        base: string | null,
        patchContent: string,
        filePath: string,
        tempGit: GitClient,
        tempDir: string,
        sourceFilePath?: string,
    ) => Promise<string | null>;
    isPatchAlreadyApplied: (
        patchContent: string,
        filePath: string,
        content: string,
        tempGit: GitClient,
        tempDir: string,
    ) => Promise<boolean>;
}

export async function buildMergePlan(args: BuildMergePlanArgs): Promise<MergePlan> {
    const {
        inputs,
        patch,
        filePath,
        accumulator,
        tempGit,
        tempDir,
        applyPatchToContent,
        isPatchAlreadyApplied,
    } = args;
    const { base, ours, resolvedPath, renameSourcePath, ghostTheirs } = inputs;

    let theirs: string | null = ghostTheirs;
    const ghostReconstructed = ghostTheirs != null;

    // Snapshot-vs-reconstruction selection.
    //
    // **Snapshot-as-primary** when the patch owns its file (no other patch in
    // this run touches it): the stored `theirs_snapshot` IS the customer's
    // intent for the file post-regen. Using it directly bypasses line-anchored
    // reconstruction and survives generator structural changes that would
    // invalidate the diff's line anchors. Architectural fix for the
    // silent-loss class of bugs.
    //
    // **Reconstruction-as-primary** when the patch shares its file with
    // another patch in this run (cross-patch isolation). Snapshots from commit
    // trees are CUMULATIVE across the patches touching a shared file, so using
    // a later patch's snapshot as its individual THEIRS would propagate prior
    // patches' edits into the merge — surfacing nested conflicts at regions
    // the patch alone never touched. The line-anchored diff produces each
    // patch's INDIVIDUAL contribution, which is what the cross-patch flow
    // needs.
    //
    // For shared-file patches, snapshot is still used as a narrow
    // markers-only fallback: when reconstruction produces marker-laden
    // content (cross-patch context mismatch where the diff applies fuzzy-
    // cleanly but yields stale-base markers), snapshot rescues. The
    // pure-null-return case (strict context mismatch — patch can't apply
    // against this base at all) is left as "skipped" so legacy paths
    // (by-association gate, accumulator fallback) handle recovery with their
    // established UX.
    const snapshotForFile =
        patch.theirs_snapshot?.[resolvedPath] ?? patch.theirs_snapshot?.[filePath];
    const fileIsShared = accumulator.isShared(resolvedPath) || accumulator.isShared(filePath);
    const useSnapshotAsPrimary = !fileIsShared && snapshotForFile != null;

    // Track which path produced theirs (for the `kind` discriminant). The
    // marker-rescue case keeps "reconstructed" because reconstruction was the
    // primary attempt; snapshot only rescued its output.
    let primarySource: "ghost" | "snapshot" | "reconstructed" = ghostReconstructed
        ? "ghost"
        : "reconstructed";

    if (!ghostReconstructed) {
        if (useSnapshotAsPrimary) {
            // Snapshot IS THEIRS. Skip the brittle reconstruction step entirely.
            // 3-way merge below uses BASE → snapshot for "what the customer
            // changed", which works on content (not line anchors) and survives
            // generator structural changes.
            theirs = snapshotForFile;
            primarySource = "snapshot";
        } else {
            theirs = await applyPatchToContent(
                base,
                patch.patch_content,
                filePath,
                tempGit,
                tempDir,
                renameSourcePath,
            );
            const reconstructionHasMarkers = hasConflictMarkerStrings(theirs);
            const baseHadMarkers = hasConflictMarkerStrings(base);
            if (reconstructionHasMarkers && !baseHadMarkers && snapshotForFile != null) {
                theirs = snapshotForFile;
            }
        }
    }

    // Detect markers that bled into THEIRS from a cross-patch context
    // mismatch: the patch's diff was authored against post-A state but we
    // tried to apply it to gen-anchor BASE. When this happens AND a prior
    // patch's accumulator entry is available with the right base_generation,
    // we want the accumulator fallback below to retry against the
    // accumulator. Treat THEIRS as null in that case so the existing fallback
    // logic fires naturally.
    //
    // Only skip outright (with `stale-conflict-markers`) when there's no
    // accumulator to fall back on — that's the original "stale markers from
    // a crashed run" case.
    const accumulatorEntry = accumulator.lookup(resolvedPath);
    if (theirs) {
        const theirsHasMarkers = hasConflictMarkerStrings(theirs);
        const baseHasMarkers = hasConflictMarkerStrings(base);
        if (theirsHasMarkers && !baseHasMarkers) {
            if (accumulatorEntry) {
                // Accumulator-based fallback below will try against
                // post-prior-patch state.
                theirs = null;
            } else {
                return { kind: "skipped", reason: "stale-conflict-markers" };
            }
        }
    }

    // Fall back to accumulator as merge base for incremental patches.
    let useAccumulatorAsMergeBase = false;

    if (accumulatorEntry && (theirs == null || base == null)) {
        theirs = await applyPatchToContent(
            accumulatorEntry.content,
            patch.patch_content,
            filePath,
            tempGit,
            tempDir,
        );
        if (theirs != null) {
            useAccumulatorAsMergeBase = true;
        } else if (
            await isPatchAlreadyApplied(
                patch.patch_content,
                filePath,
                accumulatorEntry.content,
                tempGit,
                tempDir,
            )
        ) {
            // The patch's additions are already present in the accumulator.
            // This happens when preGenerationRebase's content refresh absorbs
            // a new user commit into an existing patch, and detectNewPatches
            // also creates a separate patch for that same commit. The
            // effective THEIRS is the accumulator content itself — no further
            // apply needed.
            theirs = accumulatorEntry.content;
            useAccumulatorAsMergeBase = true;
        }
    }

    // Re-check after accumulator fallback — git apply --3way against
    // accumulated content can also produce conflict markers.
    if (theirs) {
        const theirsHasMarkers = hasConflictMarkerStrings(theirs);
        const accBaseHasMarkers =
            accumulatorEntry != null && hasConflictMarkerStrings(accumulatorEntry.content);
        if (theirsHasMarkers && !accBaseHasMarkers && !hasConflictMarkerStrings(base)) {
            return { kind: "skipped", reason: "stale-conflict-markers" };
        }
    }

    // Pre-merge with accumulated customizations (skip when accumulator is
    // already serving as merge base).
    let effective_theirs = theirs;
    let conflictReasonHint: ConflictReason | undefined;

    if (theirs != null && base != null && !useAccumulatorAsMergeBase) {
        if (accumulatorEntry && accumulatorEntry.baseGeneration === patch.base_generation) {
            // Pre-merge: combine previous customizations with current patch.
            try {
                const preMerged = threeWayMerge(base, accumulatorEntry.content, theirs);
                if (!preMerged.hasConflicts) {
                    // Clean merge — use the combined result.
                    effective_theirs = preMerged.content;
                } else {
                    // Pre-merge has conflicts — skip accumulator for this
                    // patch to avoid nested conflict markers. User patches
                    // conflict with each other and require manual resolution.
                    effective_theirs = theirs;
                }
            } catch {
                // If pre-merge fails unexpectedly, fall back to current_theirs.
                effective_theirs = theirs;
            }
        } else if (accumulatorEntry) {
            // Base generations differ — accumulator pre-merge skipped. The
            // main merge below will still run, but if it conflicts we flag it
            // as a base-generation-mismatch.
            conflictReasonHint = "base-generation-mismatch";
        }
    }

    // ===== Variant selection =====
    //
    // Order mirrors the pre-refactor branch order at lines 593-708:
    //   1. new-file paths (require effective_theirs != null).
    //   2. missing-content guards.
    //   3. accumulator-base path.
    //   4. main merge against base (ghost / snapshot / reconstructed).

    if (base == null && ours == null && effective_theirs != null) {
        return { kind: "new-file-only-user", theirs: effective_theirs };
    }
    if (base == null && ours != null && effective_theirs != null && !useAccumulatorAsMergeBase) {
        // Forward the unreachable-base signal so Phase 3 can warn: a null base
        // caused by an out-of-window prior-generation tree means this is most
        // likely an edited-existing-file patch misclassified as both-created,
        // and the customer's edit is at risk of being dropped silently.
        return {
            kind: "new-file-both",
            theirs: effective_theirs,
            ...(inputs.baseTreeUnreachable ? { baseTreeUnreachable: true } : {}),
        };
    }

    if (effective_theirs == null) {
        return { kind: "skipped", reason: "missing-content" };
    }
    if ((base == null && !useAccumulatorAsMergeBase) || ours == null) {
        return { kind: "skipped", reason: "missing-content" };
    }

    if (useAccumulatorAsMergeBase) {
        // Invariant: useAccumulatorAsMergeBase is set only inside the
        // `if (accumulatorEntry && ...)` block above, so accumulatorEntry
        // is non-null here. The fallback to base preserves pre-refactor
        // behavior at line 645 in the unreachable case.
        const mergeBase = accumulatorEntry?.content ?? base;
        if (mergeBase == null) {
            return { kind: "skipped", reason: "missing-content" };
        }
        return {
            kind: "accumulator-base",
            theirs: effective_theirs,
            mergeBase,
            conflictReasonHint,
        };
    }

    // base and ours are both non-null at this point.
    return {
        kind: primarySource,
        base: base!,
        theirs: effective_theirs,
        mergeBase: base!,
        conflictReasonHint,
    };
}
